import { useEffect, useState } from 'react'
import { onTraffic, type TrafficRecord } from '../lib/api'

/**
 * "Under the Hood", zone 4.
 *
 * Subscribes to every API call the page makes and shows the last few, verbatim.
 * This is the component that makes the whole project's claim literally true:
 * the lesson about HTTP shows you the HTTP you just caused.
 */
export default function TrafficPanel({
  title = 'Under the hood',
  empty = 'Interact with the page and the real traffic shows up here.',
  limit = 4,
}: {
  title?: string
  empty?: string
  limit?: number
}) {
  const [records, setRecords] = useState<TrafficRecord[]>([])
  const [open, setOpen] = useState(true)

  useEffect(() => onTraffic((r) => setRecords((prev) => [r, ...prev].slice(0, limit))), [limit])

  return (
    <section className="traffic">
      <button
        type="button"
        className="traffic-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="traffic-title">🔍 {title}</span>
        <span className="dim">{open ? 'hide' : 'show'}</span>
      </button>

      {open && (
        <div className="traffic-body">
          {records.length === 0 && <p className="dim">{empty}</p>}

          {records.map((r, i) => (
            <article key={`${r.at}-${i}`} className="traffic-record">
              <header>
                <code className={`method method-${r.method.toLowerCase()}`}>{r.method}</code>
                <code className="traffic-url">{r.url}</code>
                <span className={`status status-${Math.floor(r.status / 100)}`}>{r.status}</span>
                <span className="dim">{r.ms}ms</span>
              </header>

              {r.requestBody != null && (
                <>
                  <h4>Request body</h4>
                  <pre>{JSON.stringify(redact(r.requestBody), null, 2)}</pre>
                </>
              )}

              <h4>Response body</h4>
              <pre>{JSON.stringify(r.responseBody, null, 2)}</pre>

              {r.requestId && (
                <p className="dim">
                  X-Request-Id: <code>{r.requestId}</code> — this is the string to quote in a bug
                  report.
                </p>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * Never render a password back to the screen, even the user's own, even in a
 * teaching panel. Screens get shared, recorded, and screenshotted.
 */
function redact(body: unknown): unknown {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const clone: Record<string, unknown> = { ...(body as Record<string, unknown>) }
    for (const key of Object.keys(clone)) {
      if (/password|secret|token/i.test(key)) clone[key] = '••••••••'
    }
    return clone
  }
  return body
}
